import { useState, useEffect, useRef, useMemo } from 'react';
import { APIProvider, Map, Marker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { useEmergencyStore, getAlertTypeColor } from '@/stores/emergency-store';
import { useThemeStore } from '@/stores/theme-store';
import type { Alert, Patrol, ClientLive, SiteMarker, SiteSeverity } from '@/stores/emergency-store';import { AlertTriangle, Circle, X, Navigation } from 'lucide-react';
import { supabase } from '@/services/supabase';
import { MARKER_ICONS, isMarkerIconKey, initialsFromName } from '@asi/shared';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

function formatSeniority(createdAt: string): string {
  const months = (new Date().getFullYear() - new Date(createdAt).getFullYear()) * 12
    + (new Date().getMonth() - new Date(createdAt).getMonth());
  if (months < 1) return 'nuevo';
  if (months < 12) return `${months} mes${months !== 1 ? 'es' : ''}`;
  const y = Math.floor(months / 12), m = months % 12;
  return m > 0 ? `${y}a ${m}m` : `${y} año${y !== 1 ? 's' : ''}`;
}
const GUATEMALA_CENTER = { lat: 14.6349, lng: -90.5069 };

// Map style type
type MapStyle = {
  elementType?: string;
  featureType?: string;
  stylers: { color?: string; visibility?: string }[];
};

// Common: hide all default Google POI/transit clutter so client/patrol/alert
// markers are the only icons on the map. Parks stay visible (geometry only).
const HIDE_POI_STYLES: MapStyle[] = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit.station', stylers: [{ visibility: 'off' }] },
];

// Dark map styles
const DARK_MAP_STYLES: MapStyle[] = [
  ...HIDE_POI_STYLES,
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#263c3f' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#f3d19c' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] },
];

// Light map styles (minimal - mostly defaults with subtle adjustments)
const LIGHT_MAP_STYLES: MapStyle[] = [
  ...HIDE_POI_STYLES,
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5f5e0' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9d7e8' }] },
];

// Toggleable Google Maps TrafficLayer overlay. Mounted/unmounted by props.
function TrafficOverlay({ enabled }: { enabled: boolean }) {
  const map = useMap();
  useEffect(() => {
    const g = (window as unknown as { google?: { maps?: { TrafficLayer?: new () => { setMap: (m: unknown) => void } } } }).google;
    if (!map || !enabled || !g?.maps?.TrafficLayer) return;
    const layer = new g.maps.TrafficLayer();
    layer.setMap(map);
    return () => layer.setMap(null);
  }, [map, enabled]);
  return null;
}

// Polyline drawn between alert and assigned patrol on the mini-map.
// Calls the get-route edge function (same as mobile apps) to get a real
// driving route via Google Directions API on the server-side backend key.
// Falls back to a straight line if the function returns null or errors.
function RouteOverlay({ origin, destination, color }: { origin: { lat: number; lng: number }; destination: { lat: number; lng: number }; color: string }) {
  const map = useMap();
  useEffect(() => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const g = (window as unknown as { google?: any }).google;
    if (!map || !g?.maps) return;
    let cancelled = false;
    let polyline: { setMap: (m: unknown) => void } | null = null;

    const draw = (path: { lat: number; lng: number }[]) => {
      if (cancelled || !map) return;
      polyline = new g.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: 4,
        map,
      });
      const bounds = new g.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds, 60);
    };

    supabase.functions
      .invoke('get-route', { body: { origin, destination } })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && Array.isArray(data?.path) && data.path.length > 1) {
          draw(data.path as { lat: number; lng: number }[]);
        } else {
          draw([origin, destination]);
        }
      })
      .catch(() => {
        if (!cancelled) draw([origin, destination]);
      });

    return () => {
      cancelled = true;
      polyline?.setMap(null);
    };
    /* eslint-enable @typescript-eslint/no-explicit-any */
  }, [map, origin.lat, origin.lng, destination.lat, destination.lng, color]);

  return null;
}

// Easing functions
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Replicates Leaflet's flyTo behaviour for Google Maps:
// position AND zoom interpolate simultaneously each rAF frame via moveCamera(),
// with zoom following a parabolic arc (out at midpoint, in at destination).
function MapController({ selectedAlert, selectedPatrol, selectedClient, selectedSite }: { selectedAlert: Alert | null; selectedPatrol: Patrol | null; selectedClient: ClientLive | null; selectedSite: SiteMarker | null }) {
  const map = useMap();
  const rAFRef = useRef<number | null>(null);

  const cancelFlight = () => {
    if (rAFRef.current !== null) {
      cancelAnimationFrame(rAFRef.current);
      rAFRef.current = null;
    }
  };

  const flyTo = (targetLat: number, targetLng: number) => {
    if (!map || (targetLat === 0 && targetLng === 0)) return;
    cancelFlight();

    const startCenter = map.getCenter();
    if (!startCenter) return;

    const startLat = startCenter.lat();
    const startLng = startCenter.lng();
    const startZoom = map.getZoom() ?? 12;
    const targetZoom = 16;

    // Midpoint zoom: lower = more dramatic. Scale with distance so nearby
    // targets don't zoom out unnecessarily.
    const dlat = targetLat - startLat;
    const dlng = targetLng - startLng;
    const dist = Math.sqrt(dlat * dlat + dlng * dlng);
    const midZoom = dist < 0.005
      ? (startZoom + targetZoom) / 2          // very close — gentle arc
      : Math.max(targetZoom - 4 - Math.log2(1 + dist * 40), 8);

    const DURATION_MS = 2000;
    const startTime = performance.now();

    const tick = (now: number) => {
      const raw = Math.min((now - startTime) / DURATION_MS, 1);

      // Position: single smooth ease across the whole duration
      const posT = easeInOutCubic(raw);
      const lat = startLat + (targetLat - startLat) * posT;
      const lng = startLng + (targetLng - startLng) * posT;

      // Zoom: parabolic arc — first half eases to midZoom, second half to targetZoom
      let zoom: number;
      if (raw < 0.5) {
        zoom = startZoom + (midZoom - startZoom) * easeInOutCubic(raw * 2);
      } else {
        zoom = midZoom + (targetZoom - midZoom) * easeInOutCubic((raw - 0.5) * 2);
      }

      // moveCamera sets position AND zoom atomically in one frame — no competing animations
      map.moveCamera({ center: { lat, lng }, zoom });

      if (raw < 1) {
        rAFRef.current = requestAnimationFrame(tick);
      } else {
        rAFRef.current = null;
      }
    };

    rAFRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    if (selectedAlert) {
      flyTo(selectedAlert.latitude, selectedAlert.longitude);
    } else if (selectedPatrol) {
      flyTo(selectedPatrol.latitude, selectedPatrol.longitude);
    } else if (selectedClient) {
      flyTo(selectedClient.latitude, selectedClient.longitude);
    } else if (selectedSite) {
      flyTo(selectedSite.latitude, selectedSite.longitude);
    }
    return cancelFlight;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedAlert, selectedPatrol, selectedClient, selectedSite]);

  return null;
}

interface EmergencyMapProps {
  className?: string;
}

// SVG marker icons as data URLs
function createAlertMarkerIcon(color: string): string {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="14" fill="${color}" stroke="white" stroke-width="3"/>
      <text x="16" y="21" text-anchor="middle" fill="white" font-size="14" font-weight="bold">!</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function createPatrolMarkerIcon(color: string, iconKey: string = 'motorcycle'): string {
  // Patrol marker uses the same shared icon set as clients so operators can
  // tell patrols apart by their assigned glyph. Wrapped in a rounded square
  // (instead of the client's circle) so shape alone distinguishes patrol
  // from client at a glance, even when colors overlap.
  const key = isMarkerIconKey(iconKey) ? iconKey : 'motorcycle';
  const path = MARKER_ICONS[key].svgPath;
  const inner = path
    ? `<g transform="translate(6 6) scale(1.0)" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${path}"/></g>`
    : '';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
      <rect x="2" y="2" width="32" height="32" rx="6" fill="${color}" stroke="white" stroke-width="2"/>
      ${inner}
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function createClientMarkerIcon(
  color: string,
  iconKey: string = 'initials',
  fullName: string = '',
): string {
  // Colored circle background + either a white SVG glyph or initials text.
  const key = isMarkerIconKey(iconKey) ? iconKey : 'initials';

  let inner: string;
  if (key === 'initials') {
    const text = initialsFromName(fullName)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // Font size shrinks slightly for 2-char initials so they fit cleanly.
    const fontSize = text.length >= 2 ? 13 : 16;
    inner = `<text x="16" y="16" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="700" fill="white">${text}</text>`;
  } else {
    const path = MARKER_ICONS[key].svgPath;
    inner = `<g transform="translate(5 5) scale(0.92)" fill="white" stroke="none"><path d="${path}"/></g>`;
  }

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="14" fill="${color}" stroke="white" stroke-width="2"/>
      ${inner}
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Brand-aligned palette: navy is the resting state (no open events / patrol
// dispatched and en route), red marks any open activity, and amber flags
// priority sites with several simultaneous events. The legacy 'info' and
// 'warning' tiers map to red as well — once an event is opened the site
// stops being neutral, regardless of the CID severity.
const SITE_TIER_COLORS: Record<SiteSeverity, string> = {
  idle:       '#1e3a8a', // navy-900 — brand colour, resting state
  info:       '#dc2626', // red-600
  warning:    '#dc2626', // red-600
  alarm:      '#b91c1c', // red-700
  critical:   '#f59e0b', // amber-500 — priority (high event volume)
  dispatched: '#1e3a8a', // navy-900 — back to base while patrol attends
};

function createSiteMarkerIcon(severity: SiteSeverity, count: number): string {
  const color = SITE_TIER_COLORS[severity];
  const badge = count > 0
    ? `<g><circle cx="22" cy="6" r="6" fill="#ffffff"/><text x="22" y="9" text-anchor="middle" font-size="9" font-weight="700" fill="${color}">${count > 9 ? '9+' : count}</text></g>`
    : '';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
      <rect x="2" y="2" width="24" height="24" rx="5" fill="${color}" stroke="white" stroke-width="2"/>
      <path d="M14 7 L21 13 V20 H17 V15 H11 V20 H7 V13 Z" fill="white" />
      ${badge}
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function getClientStatusColor(status: ClientLive['status'], updatedAt: string, staleSec: number): string {
  // Trust the status field reported by the device. Only fall back to gray when
  // the device explicitly reported OFFLINE or hasn't checked in for a long
  // time (3× the stale threshold) — in that case the device probably crashed
  // or lost connectivity without sending an OFFLINE flag.
  //
  // Palette is intentionally distinct from patrols (green/blue/amber) and
  // alerts (red/orange) so client markers are never mistaken for either.
  //   ONLINE      → violet  (app open, foreground)
  //   BACKGROUND  → cyan    (app minimized, still tracking)
  //   OFFLINE     → slate   (no signal / app closed)
  const ageSec = (Date.now() - new Date(updatedAt).getTime()) / 1000;
  const veryStale = ageSec > staleSec * 3;
  if (status === 'OFFLINE' || veryStale) return '#94a3b8'; // slate-400
  if (status === 'BACKGROUND') return '#06b6d4';            // cyan-500
  return '#8b5cf6';                                         // violet-500 (online)
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    PENDING: 'Pendiente',
    ASSIGNED: 'Asignada',
    EN_ROUTE: 'En camino',
    ON_SCENE: 'En sitio',
    IN_PROGRESS: 'En proceso',
    AVAILABLE: 'Disponible',
  };
  return labels[status] || status;
}

function getAlertTypeName(type: string): string {
  const names: Record<string, string> = {
    SOS: 'SOS',
    FIRE: 'Incendio',
    MEDICAL: 'Médica',
    PANIC: 'Pánico',
    INTRUSION: 'Intrusión',
  };
  return names[type] || type;
}

export function EmergencyMap({ className = '' }: EmergencyMapProps) {
  const { theme } = useThemeStore();
  const {
    mapCenter,
    activeAlerts,
    patrols,
    clients,
    sites,
    selectedAlert,
    selectedPatrol,
    selectedClient,
    selectedSite,
    selectedClientDetails,
    selectedClientDetailsLoading,
    selectAlert,
    selectPatrol,
    selectClient,
    selectSite,
  } = useEmergencyStore();

  const [hoveredAlert, setHoveredAlert] = useState<Alert | null>(null);
  const [hoveredPatrol, setHoveredPatrol] = useState<Patrol | null>(null);
  const [hoveredClient, setHoveredClient] = useState<ClientLive | null>(null);
  const [hoveredSite, setHoveredSite] = useState<SiteMarker | null>(null);
  const [staleSec, setStaleSec] = useState<number>(300);
  const [showClients, setShowClients] = useState<boolean>(true);
  const [showTraffic, setShowTraffic] = useState<boolean>(false);
  const [miniMapOpen, setMiniMapOpen] = useState<boolean>(true);
  // Tick every 5s so getClientStatusColor / getPatrolColor re-evaluate
  // staleness even when no Realtime event arrives. Without this, a client
  // whose updated_at row is fresh but whose status field changed via a
  // dropped event would stay the wrong color until the next 15s poll.
  const [, setStaleTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStaleTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  // Stale threshold (seconds) comes from tracking_settings.client.
  // Loaded once on mount; admins can change it from Settings page.
  useEffect(() => {
    let cancelled = false;
    supabase
      .from('tracking_settings')
      .select('stale_threshold_seconds')
      .eq('role', 'client')
      .single()
      .then(({ data }) => {
        if (!cancelled && data?.stale_threshold_seconds) {
          setStaleSec(data.stale_threshold_seconds);
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Select map styles based on theme
  const mapStyles = theme === 'dark' ? DARK_MAP_STYLES : LIGHT_MAP_STYLES;

  // Default center from store
  const defaultCenter = { lat: mapCenter.latitude, lng: mapCenter.longitude };

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className={`relative rounded-xl overflow-hidden bg-slate-800 flex items-center justify-center ${className}`}>
        <div className="text-center p-8">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h3 className="text-white font-medium mb-2">API Key no configurada</h3>
          <p className="text-slate-400 text-sm">
            Configure VITE_GOOGLE_MAPS_API_KEY en el archivo .env
          </p>
        </div>
      </div>
    );
  }

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className={`relative ${className}`}>
        <Map
          defaultCenter={defaultCenter}
          defaultZoom={12}
          disableDefaultUI={false}
          zoomControl={true}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          gestureHandling="greedy"
          className="w-full h-full"
          styles={mapStyles}
          onClick={() => {
            // Click on empty map area dismisses sticky InfoWindows.
            const store = useEmergencyStore.getState();
            if (store.selectedClient) store.selectClient(null);
            if (store.selectedPatrol) store.selectPatrol(null);
          }}
        >
          {/* Controller for panning when selection changes */}
          <MapController selectedAlert={selectedAlert} selectedPatrol={selectedPatrol} selectedClient={selectedClient} selectedSite={selectedSite} />
          <TrafficOverlay enabled={showTraffic} />
          
          {/* Alert markers */}
          {activeAlerts.map((alert) => {
            const color = getAlertTypeColor(alert.type);
            const isSelected = selectedAlert?.id === alert.id;

            return (
              <Marker
                key={alert.id}
                position={{ lat: alert.latitude, lng: alert.longitude }}
                onClick={() => selectAlert(alert)}
                onMouseOver={() => setHoveredAlert(alert)}
                onMouseOut={() => setHoveredAlert(null)}
                icon={{
                  url: createAlertMarkerIcon(color),
                  scaledSize: (isSelected ? { width: 44, height: 44 } : { width: 32, height: 32 }) as unknown as google.maps.Size,
                  anchor: (isSelected ? { x: 22, y: 22 } : { x: 16, y: 16 }) as unknown as google.maps.Point,
                }}
                title={`${getAlertTypeName(alert.type)} - #${alert.ticket_number}`}
              />
            );
          })}

          {/* Alert info window */}
          {hoveredAlert && (
            <InfoWindow
              position={{ lat: hoveredAlert.latitude, lng: hoveredAlert.longitude }}
              pixelOffset={[0, -20]}
              onCloseClick={() => setHoveredAlert(null)}
            >
              <div className="p-2 min-w-[150px]">
                <div className="font-bold text-gray-900">{getAlertTypeName(hoveredAlert.type)}</div>
                <div className="text-sm text-gray-600">#{hoveredAlert.ticket_number}</div>
                <div className="text-sm text-gray-500 mt-1">{getStatusLabel(hoveredAlert.status)}</div>
                {hoveredAlert.client_name && (
                  <div className="text-sm text-gray-700 mt-1">{hoveredAlert.client_name}</div>
                )}
              </div>
            </InfoWindow>
          )}

          {/* Patrol markers — skip patrols without a real GPS fix; the DB
              seeds (0,0) for newly-created patrol profiles, and we don't
              want them showing up in the Atlantic ocean. */}
          {patrols
            .filter(
              (p) =>
                Number.isFinite(p.latitude) &&
                Number.isFinite(p.longitude) &&
                !(p.latitude === 0 && p.longitude === 0),
            )
            .map((patrol) => {
            const color = patrol.status === 'AVAILABLE' ? '#22c55e' :
                         patrol.status === 'BACKGROUND' ? '#06b6d4' :
                         patrol.status === 'EN_ROUTE' ? '#3b82f6' :
                         patrol.status === 'OFFLINE' ? '#94a3b8' : '#f59e0b';
            const isSelected = selectedPatrol?.id === patrol.id;

            return (
              <Marker
                key={patrol.id}
                position={{ lat: patrol.latitude, lng: patrol.longitude }}
                onClick={() => selectPatrol(patrol)}
                onMouseOver={() => setHoveredPatrol(patrol)}
                onMouseOut={() => setHoveredPatrol(null)}
                icon={{
                  url: createPatrolMarkerIcon(color, patrol.marker_icon),
                  scaledSize: (isSelected ? { width: 40, height: 40 } : { width: 28, height: 28 }) as unknown as google.maps.Size,
                  anchor: (isSelected ? { x: 20, y: 20 } : { x: 14, y: 14 }) as unknown as google.maps.Point,
                }}
                title={patrol.name}
              />
            );
          })}

          {/* Patrol hover info window */}
          {hoveredPatrol && hoveredPatrol.id !== selectedPatrol?.id && (
            <InfoWindow
              position={{ lat: hoveredPatrol.latitude, lng: hoveredPatrol.longitude }}
              pixelOffset={[0, -15]}
              onCloseClick={() => setHoveredPatrol(null)}
            >
              <div className="p-2 min-w-[120px]">
                <div className="font-bold text-gray-900">{hoveredPatrol.name}</div>
                <div className="text-sm text-gray-500">{getStatusLabel(hoveredPatrol.status)}</div>
              </div>
            </InfoWindow>
          )}

          {/* Sticky patrol info window (click) */}
          {selectedPatrol && (
            <InfoWindow
              position={{ lat: selectedPatrol.latitude, lng: selectedPatrol.longitude }}
              pixelOffset={[0, -20]}
              headerDisabled
              onCloseClick={() => useEmergencyStore.getState().selectPatrol(null)}
            >
              <div className="p-2 min-w-[220px] max-w-[280px] text-gray-900">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="font-semibold text-base">{selectedPatrol.name}</div>
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor:
                        selectedPatrol.status === 'AVAILABLE' ? '#dcfce7'
                        : selectedPatrol.status === 'EN_ROUTE' ? '#dbeafe'
                        : selectedPatrol.status === 'ON_SCENE' ? '#fef9c3'
                        : selectedPatrol.status === 'BACKGROUND' ? '#cffafe'
                        : '#e2e8f0',
                      color:
                        selectedPatrol.status === 'AVAILABLE' ? '#15803d'
                        : selectedPatrol.status === 'EN_ROUTE' ? '#1d4ed8'
                        : selectedPatrol.status === 'ON_SCENE' ? '#a16207'
                        : selectedPatrol.status === 'BACKGROUND' ? '#0e7490'
                        : '#475569',
                    }}
                  >
                    {getStatusLabel(selectedPatrol.status)}
                  </span>
                </div>
                {selectedPatrol.phone && (
                  <a href={`tel:${selectedPatrol.phone}`} className="text-xs text-blue-600 hover:underline block mb-1">
                    📞 {selectedPatrol.phone}
                  </a>
                )}
                <div className="text-[11px] text-gray-400 mt-1">
                  🕒 {new Date(selectedPatrol.last_update).toLocaleTimeString('es-GT')}
                </div>
              </div>
            </InfoWindow>
          )}

          {/* Alarm-panel site markers — gray when idle, escalating colours
              with a small white badge showing the open-event count. Click
              opens the right-panel ARC event detail. */}
          {sites
            .filter(
              (s) =>
                Number.isFinite(s.latitude) &&
                Number.isFinite(s.longitude) &&
                !(s.latitude === 0 && s.longitude === 0),
            )
            .map((site) => {
            const isSelected = selectedSite?.id === site.id;
            return (
              <Marker
                key={`site-${site.id}`}
                position={{ lat: site.latitude, lng: site.longitude }}
                onClick={() => selectSite(site)}
                onMouseOver={() => setHoveredSite(site)}
                onMouseOut={() => setHoveredSite(null)}
                icon={{
                  url: createSiteMarkerIcon(site.max_severity, site.open_events_count),
                  scaledSize: (isSelected ? { width: 36, height: 36 } : { width: 24, height: 24 }) as unknown as google.maps.Size,
                  anchor: (isSelected ? { x: 18, y: 18 } : { x: 12, y: 12 }) as unknown as google.maps.Point,
                }}
                title={site.name}
              />
            );
          })}

          {hoveredSite && (
            <InfoWindow
              position={{ lat: hoveredSite.latitude, lng: hoveredSite.longitude }}
              pixelOffset={[0, -18]}
              onCloseClick={() => setHoveredSite(null)}
            >
              <div className="p-2 min-w-[180px]">
                <div className="font-bold text-gray-900">
                  {hoveredSite.account_name ? `${hoveredSite.account_name} \u00b7 ${hoveredSite.name}` : hoveredSite.name}
                </div>
                {hoveredSite.account_number && (
                  <div className="text-xs text-gray-500">Cuenta {hoveredSite.account_number}</div>
                )}
                {hoveredSite.address && (
                  <div className="text-xs text-gray-600 mt-1">{hoveredSite.address}</div>
                )}
                {hoveredSite.open_events_count > 0 ? (
                  <div className="text-xs text-red-600 mt-1 font-semibold">
                    {hoveredSite.open_events_count} evento{hoveredSite.open_events_count > 1 ? 's' : ''} abierto{hoveredSite.open_events_count > 1 ? 's' : ''}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 mt-1">Sin eventos activos</div>
                )}
              </div>
            </InfoWindow>
          )}

          {/* Live client markers */}
          {showClients && clients.map((client) => {
            const color = getClientStatusColor(client.status, client.last_update, staleSec);
            const isSelected = selectedClient?.id === client.id;
            return (
              <Marker
                key={client.id}
                position={{ lat: client.latitude, lng: client.longitude }}
                onClick={() => selectClient(client)}
                onMouseOver={() => setHoveredClient(client)}
                onMouseOut={() => setHoveredClient(null)}
                icon={{
                  url: createClientMarkerIcon(color, client.marker_icon, client.name),
                  scaledSize: (isSelected ? { width: 36, height: 36 } : { width: 26, height: 26 }) as unknown as google.maps.Size,
                  anchor: (isSelected ? { x: 18, y: 18 } : { x: 13, y: 13 }) as unknown as google.maps.Point,
                }}
                title={client.name}
              />
            );
          })}

          {hoveredClient && hoveredClient.id !== selectedClient?.id && (
            <InfoWindow
              position={{ lat: hoveredClient.latitude, lng: hoveredClient.longitude }}
              pixelOffset={[0, -15]}
              onCloseClick={() => setHoveredClient(null)}
            >
              <div className="p-2 min-w-[160px]">
                <div className="font-bold text-gray-900">{hoveredClient.name}</div>
                <div className="text-sm text-gray-500">
                  {hoveredClient.status === 'ONLINE' ? 'En línea' : hoveredClient.status === 'BACKGROUND' ? 'Segundo plano' : 'Desconectado'}
                </div>
                {hoveredClient.battery_level !== null && (
                  <div className="text-xs text-gray-500 mt-1">
                    Batería: {hoveredClient.battery_level}% {hoveredClient.is_charging ? '⚡' : ''}
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-1">
                  {new Date(hoveredClient.last_update).toLocaleTimeString('es-GT')}
                </div>
              </div>
            </InfoWindow>
          )}

          {/* Sticky info window for the client the operator clicked.
              Shows the enriched account memberships fetched in selectClient(). */}
          {selectedClient && (
            <InfoWindow
              position={{ lat: selectedClient.latitude, lng: selectedClient.longitude }}
              pixelOffset={[0, -18]}
              headerDisabled
              onCloseClick={() => useEmergencyStore.getState().selectClient(null)}
            >
              <div className="p-2 min-w-[260px] max-w-[320px] text-gray-900">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="font-semibold text-base">{selectedClient.name}</div>
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor:
                        selectedClient.status === 'ONLINE' ? '#ede9fe'
                        : selectedClient.status === 'BACKGROUND' ? '#cffafe'
                        : '#e2e8f0',
                      color:
                        selectedClient.status === 'ONLINE' ? '#6d28d9'
                        : selectedClient.status === 'BACKGROUND' ? '#0e7490'
                        : '#475569',
                    }}
                  >
                    {selectedClient.status === 'ONLINE' ? 'En línea' : selectedClient.status === 'BACKGROUND' ? 'Segundo plano' : 'Desconectado'}
                  </span>
                </div>

                {selectedClient.phone && (
                  <a href={`tel:${selectedClient.phone}`} className="text-xs text-blue-600 hover:underline block mb-1">
                    📞 {selectedClient.phone}
                  </a>
                )}

                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-600 mb-2">
                  {selectedClient.battery_level != null && (
                    <span>🔋 {selectedClient.battery_level}%{selectedClient.is_charging ? ' ⚡' : ''}</span>
                  )}
                  {selectedClient.accuracy != null && (
                    <span>🎯 ±{Math.round(selectedClient.accuracy)} m</span>
                  )}
                  {selectedClient.speed != null && selectedClient.speed > 0 && (
                    <span>🚶 {Math.round(selectedClient.speed * 3.6)} km/h</span>
                  )}
                  <span>🕒 {new Date(selectedClient.last_update).toLocaleTimeString('es-GT')}</span>
                </div>

                {/* Account memberships */}
                <div className="border-t border-gray-200 pt-2">
                  <div className="text-[10px] uppercase font-medium text-gray-500 mb-1">Cuentas</div>
                  {selectedClientDetailsLoading && (
                    <div className="text-[11px] text-gray-500 italic">Cargando…</div>
                  )}
                  {!selectedClientDetailsLoading && (selectedClientDetails?.accounts.length ?? 0) === 0 && (
                    <div className="text-[11px] text-gray-500 italic">Sin cuentas asociadas</div>
                  )}
                  {selectedClientDetails?.accounts.map((acc) => (
                    <div key={acc.id} className="mb-1.5 last:mb-0">
                      <div className="flex items-center justify-between gap-2">
                        <a
                          href={`/accounts/${acc.id}`}
                          className="text-xs font-medium text-blue-600 hover:underline truncate"
                        >
                          {acc.name}
                        </a>
                        <div className="flex items-center gap-1 shrink-0">
                          {acc.client_tier && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
                              acc.client_tier === 'A' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                              acc.client_tier === 'B' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                              'bg-slate-100 text-slate-600 border-slate-300'
                            }`}>
                              {acc.client_tier}
                            </span>
                          )}
                          <span className="text-[10px] text-gray-500 font-mono whitespace-nowrap">
                            {acc.account_number}
                          </span>
                        </div>
                      </div>
                      <div className="text-[11px] text-gray-600 leading-tight">
                        {acc.role === 'owner' ? '👑 Titular' : acc.role === 'admin' ? '⚙ Admin' : '👤 ' + acc.role}
                        {acc.city ? ` · ${acc.city}` : ''}
                        {acc.created_at ? ` · ${formatSeniority(acc.created_at)}` : ''}
                      </div>
                      {acc.billing_address && (
                        <div className="text-[11px] text-gray-500 truncate" title={acc.billing_address}>
                          📍 {acc.billing_address}
                        </div>
                      )}
                      {acc.phone && (
                        <div className="text-[11px] text-gray-500">📞 {acc.phone}</div>
                      )}
                    </div>
                  ))}
                  {selectedClientDetails && selectedClientDetails.open_alerts > 0 && (
                    <div className="mt-2 text-[11px] text-red-600 font-medium">
                      ⚠ {selectedClientDetails.open_alerts} alerta{selectedClientDetails.open_alerts === 1 ? '' : 's'} abierta{selectedClientDetails.open_alerts === 1 ? '' : 's'}
                    </div>
                  )}
                </div>
              </div>
            </InfoWindow>
          )}
        </Map>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-gray-200 dark:border-slate-700">
          <div className="text-xs text-gray-500 dark:text-slate-400 font-medium uppercase mb-2">Leyenda</div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300">
              <div className="w-4 h-4 bg-red-500 rounded-full" />
              <span>SOS / Pánico</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300">
              <div className="w-4 h-4 bg-orange-500 rounded-full" />
              <span>Incendio</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300">
              <div className="w-4 h-4 bg-pink-500 rounded-full" />
              <span>Médica</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300 mt-2 pt-2 border-t border-gray-200 dark:border-slate-700">
              <div className="w-4 h-4 bg-green-500 rounded" />
              <span>Patrulla disponible</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300">
              <div className="w-4 h-4 bg-blue-500 rounded" />
              <span>Patrulla en camino</span>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-slate-700 space-y-1.5">
              <button
                onClick={() => setShowClients((v) => !v)}
                className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  showClients
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                }`}
                title={showClients ? 'Ocultar clientes en el mapa' : 'Mostrar clientes en el mapa'}
              >
                <span>Clientes en vivo</span>
                <span>{showClients ? '✓ ON' : 'OFF'}</span>
              </button>
              <button
                onClick={() => setShowTraffic((v) => !v)}
                className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  showTraffic
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                }`}
                title={showTraffic ? 'Ocultar capa de tráfico' : 'Mostrar capa de tráfico en vivo'}
              >
                <span>Tráfico en vivo</span>
                <span>{showTraffic ? '✓ ON' : 'OFF'}</span>
              </button>
            </div>
            {/* Colors below MUST mirror getClientStatusColor() in this same
                file so the legend never lies to the operator:
                  online → violet-500, background → cyan-500, offline → slate-400 */}
            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300">
              <div className="w-4 h-4 rounded-full bg-violet-500 border-2 border-white" />
              <span>Cliente en línea</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300">
              <div className="w-4 h-4 rounded-full bg-cyan-500 border-2 border-white" />
              <span>Cliente (segundo plano)</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300">
              <div className="w-4 h-4 rounded-full bg-slate-400 border-2 border-white" />
              <span>Cliente desconectado</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="absolute top-4 right-4 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Circle className="w-3 h-3 fill-red-500 text-red-500" />
              <span className="text-gray-900 dark:text-white font-medium">{activeAlerts.length}</span>
              <span className="text-gray-500 dark:text-slate-400">alertas</span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="w-3 h-3 fill-green-500 text-green-500" />
              <span className="text-gray-900 dark:text-white font-medium">{patrols.length}</span>
              <span className="text-gray-500 dark:text-slate-400">patrullas</span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="w-3 h-3 fill-blue-500 text-blue-500" />
              <span className="text-gray-900 dark:text-white font-medium">{clients.length}</span>
              <span className="text-gray-500 dark:text-slate-400">clientes</span>
            </div>
          </div>
        </div>
        {/* Uber-style mini-map for the selected alert */}
        <AlertMiniMap
          alert={selectedAlert}
          patrols={patrols}
          mapStyles={mapStyles}
          open={miniMapOpen}
          onToggle={() => setMiniMapOpen((v) => !v)}
        />
      </div>
    </APIProvider>
  );
}

function AlertMiniMap({
  alert,
  patrols,
  mapStyles,
  open,
  onToggle,
}: {
  alert: Alert | null;
  patrols: Patrol[];
  mapStyles: MapStyle[];
  open: boolean;
  onToggle: () => void;
}) {
  const patrol = useMemo(
    () => (alert?.patrol_id ? patrols.find((p) => p.id === alert.patrol_id) ?? null : null),
    [alert?.patrol_id, patrols],
  );

  if (!alert) return null;

  const alertPos = { lat: alert.latitude, lng: alert.longitude };
  // Treat (0,0) as "no fix yet". The DB seeds patrol_locations with (0,0)
  // when a patrol profile is created, so without this guard the minimap
  // would draw a route from the Atlantic ocean (origin) to Guatemala.
  const hasPatrolFix =
    !!patrol &&
    Number.isFinite(patrol.latitude) &&
    Number.isFinite(patrol.longitude) &&
    !(patrol.latitude === 0 && patrol.longitude === 0);
  const patrolPos = hasPatrolFix ? { lat: patrol!.latitude, lng: patrol!.longitude } : null;
  const alertColor = getAlertTypeColor(alert.type);

  // Center between alert and patrol (or just on alert).
  const center = patrolPos
    ? { lat: (alertPos.lat + patrolPos.lat) / 2, lng: (alertPos.lng + patrolPos.lng) / 2 }
    : alertPos;

  return (
    <div className="absolute bottom-4 right-4 w-[320px] bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800/60 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: alertColor }} />
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-900 dark:text-white truncate">
              #{alert.ticket_number} · {alert.client_name ?? 'Cliente'} · {getAlertTypeName(alert.type)}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-slate-400 truncate">
              {patrol
                ? hasPatrolFix
                  ? `Patrulla: ${patrol.name}`
                  : `Patrulla: ${patrol.name} · sin ubicación`
                : 'Sin patrulla asignada'}
            </div>
          </div>
        </div>
        <button
          onClick={onToggle}
          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-500 dark:text-slate-400"
          title={open ? 'Minimizar' : 'Expandir'}
        >
          {open ? <X className="w-3.5 h-3.5" /> : <Navigation className="w-3.5 h-3.5" />}
        </button>
      </div>
      {open && (
        <div className="h-[220px] relative">
          <Map
            key={`mini-${alert.id}`}
            defaultCenter={center}
            defaultZoom={14}
            disableDefaultUI={true}
            gestureHandling="cooperative"
            className="w-full h-full"
            styles={mapStyles}
          >
            <TrafficOverlay enabled={true} />
            {patrolPos && (
              <RouteOverlay origin={patrolPos} destination={alertPos} color={alertColor} />
            )}
            <Marker
              position={alertPos}
              icon={{
                url: createAlertMarkerIcon(alertColor),
                scaledSize: { width: 32, height: 32 } as unknown as google.maps.Size,
                anchor: { x: 16, y: 16 } as unknown as google.maps.Point,
              }}
            />
            {patrolPos && (
              <Marker
                position={patrolPos}
                icon={{
                  url: createPatrolMarkerIcon('#22c55e'),
                  scaledSize: { width: 28, height: 28 } as unknown as google.maps.Size,
                  anchor: { x: 14, y: 14 } as unknown as google.maps.Point,
                }}
              />
            )}
          </Map>
        </div>
      )}
    </div>
  );
}
